import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { Plus, Search, Edit2, Trash2, Share2, X, Check, Calendar, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Event, EventGroup } from '../types';
import EventSearch from './EventSearch';
import GroupShareModal from './GroupShareModal';
import LiquidGlass from './LiquidGlass';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

// Sortable event item component
function SortableEventItem({ event, groupId, onRemove }: { 
  event: Event, 
  groupId: string, 
  onRemove: (eventId: string) => void 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: event.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    position: isDragging ? 'relative' : undefined,
    backgroundColor: isDragging ? 'rgba(255, 255, 255, 0.8)' : undefined,
    boxShadow: isDragging ? '0 5px 15px rgba(0, 0, 0, 0.1)' : undefined
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded mb-1"
    >
      <div className="flex items-center gap-2">
        <button
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Calendar className="w-4 h-4 text-gray-500" />
        <span>{event.name}</span>
      </div>
      <button
        onClick={() => onRemove(event.id)}
        className="text-gray-400 hover:text-red-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function MultiRace() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [groups, setGroups] = useState<EventGroup[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<EventGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', date: '' });
  const [error, setError] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEventSelector, setShowEventSelector] = useState<string | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<Record<string, string[]>>({});
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const groupsPerPage = 12;
  const [activeGroupForDnd, setActiveGroupForDnd] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (user) {
      loadGroups();
      loadEvents();
    }
  }, [user, organization]);

  useEffect(() => {
    filterGroups();
  }, [searchTerm, groups]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filterGroups = () => {
    if (!searchTerm.trim()) {
      setFilteredGroups(groups);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = groups.filter(group => 
      group.name.toLowerCase().includes(term) || 
      formatDate(group.date).includes(term)
    );
    setFilteredGroups(filtered);
  };

  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      
      // First, clear any existing groups to prevent flashing
      setGroups([]);
      setFilteredGroups([]);
      
      let query = supabase
        .from('event_groups')
        .select('*')
        .order('date', { ascending: false });
        
      // If user belongs to an organization, fetch both personal and organization groups
      if (organization?.id) {
        // Get all user IDs from the organization
        const { data: orgUsers, error: orgError } = await supabase
          .from('user_profiles')
          .select('user_id')
          .eq('organization_id', organization.id);

        if (orgError) throw orgError;

        // Create array of user IDs including current user
        const userIds = [user?.id, ...(orgUsers?.map(u => u.user_id) || [])].filter(Boolean);
        
        // Use in() to query for all users in the organization
        query = query.in('user_id', userIds);
      } else {
        // Otherwise, just fetch personal groups
        query = query.eq('user_id', user?.id);
      }
      
      const { data, error } = await query;

      if (error) {
        console.error("Error loading groups:", error);
        throw error;
      }
      
      console.log("Loaded groups:", data?.length || 0);
      setGroups(data || []);
      setFilteredGroups(data || []);
      

      // Load selected events for each group
      const { data: groupEvents } = await supabase
        .from('events')
        .select('id, group_id, order_index')
        .not('group_id', 'is', null)
        .order('order_index', { ascending: true });

      if (groupEvents) {
        const eventsByGroup = groupEvents.reduce((acc, event) => {
          if (event.group_id) {
            acc[event.group_id] = [...(acc[event.group_id] || []), event.id];
          }
          return acc;
        }, {} as Record<string, string[]>);
        setSelectedEvents(eventsByGroup);
      }
    } catch (err: any) {
      console.error('Error loading groups:', err);
      setError(err.message);
    } finally {
      setLoadingGroups(false);
      setInitialLoadComplete(true);
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      let query = supabase
        .from('events')
        .select('*');
        
      // If user belongs to an organization, fetch both personal and organization events
      if (organization?.id) {
        // Get all user IDs from the organization
        const { data: orgUsers, error: orgError } = await supabase
          .from('user_profiles')
          .select('user_id')
          .eq('organization_id', organization.id);

        if (orgError) throw orgError;

        // Create array of user IDs including current user
        const userIds = [user?.id, ...(orgUsers?.map(u => u.user_id) || [])].filter(Boolean);
        
        // Use OR logic to get both user events and organization events
        query = query.or(`user_id.in.(${userIds.join(',')}),organization_id.eq.${organization.id}`);
      } else {
        // Otherwise, just fetch personal events
        query = query.eq('user_id', user?.id);
      }
      
      const { data, error } = await query.order('date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (err: any) {
      console.error('Error loading events:', err);
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('event_groups')
        .insert([{
          ...newGroup,
          user_id: user?.id
        }])
        .select()
        .single();

      if (error) throw error;
      
      setGroups(prev => [data, ...prev]);
      setFilteredGroups(prev => [data, ...prev]);
      setNewGroup({ name: '', date: '' });
      setShowNewGroup(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateName = async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('event_groups')
        .update({ name: editingName })
        .eq('id', groupId);

      if (error) throw error;
      
      setGroups(prev => prev.map(group => 
        group.id === groupId ? { ...group, name: editingName } : group
      ));
      setFilteredGroups(prev => prev.map(group => 
        group.id === groupId ? { ...group, name: editingName } : group
      ));
      setEditingGroupId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('event_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;
      
      setGroups(prev => prev.filter(group => group.id !== groupId));
      setFilteredGroups(prev => prev.filter(group => group.id !== groupId));
      setDeletingGroupId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEventSelection = async (groupId: string, eventId: string) => {
    try {
      // Get the current highest order_index for this group
      const currentEvents = selectedEvents[groupId] || [];
      const nextOrderIndex = currentEvents.length;

      // Update the database
      const { error } = await supabase
        .from('events')
        .update({ 
          group_id: groupId,
          order_index: nextOrderIndex
        })
        .eq('id', eventId);

      if (error) throw error;

      // Update local state
      setSelectedEvents(prev => ({
        ...prev,
        [groupId]: [...(prev[groupId] || []), eventId]
      }));
    } catch (err: any) {
      console.error('Error updating event selection:', err);
      setError(err.message);
    }
  };

  const handleRemoveEvent = async (groupId: string, eventId: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ 
          group_id: null,
          order_index: 0
        })
        .eq('id', eventId);

      if (error) throw error;

      // Get the removed event's index
      const currentEvents = [...(selectedEvents[groupId] || [])];
      const removedIndex = currentEvents.indexOf(eventId);
      
      // Remove the event from the array
      const updatedEvents = currentEvents.filter(id => id !== eventId);
      
      // Update local state
      setSelectedEvents(prev => ({
        ...prev,
        [groupId]: updatedEvents
      }));

      // Update order_index for all events after the removed one
      if (removedIndex >= 0) {
        const eventsToUpdate = events
          .filter(e => e.group_id === groupId && e.order_index > removedIndex)
          .map(e => ({
            id: e.id,
            order_index: e.order_index - 1
          }));

        if (eventsToUpdate.length > 0) {
          // Update each event's order_index
          for (const event of eventsToUpdate) {
            await supabase
              .from('events')
              .update({ order_index: event.order_index })
              .eq('id', event.id);
          }
        }
      }
    } catch (err: any) {
      console.error('Error removing event:', err);
      setError(err.message);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const groupId = activeGroupForDnd;
    if (!groupId) return;
    
    const currentEvents = [...(selectedEvents[groupId] || [])];
    const oldIndex = currentEvents.indexOf(active.id as string);
    const newIndex = currentEvents.indexOf(over.id as string);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Reorder the array
    const newOrder = [...currentEvents];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, active.id as string);
    
    // Update local state
    setSelectedEvents(prev => ({
      ...prev,
      [groupId]: newOrder
    }));
    
    // Update order_index for all affected events
    try {
      for (let i = 0; i < newOrder.length; i++) {
        await supabase
          .from('events')
          .update({ order_index: i })
          .eq('id', newOrder[i]);
      }
    } catch (err: any) {
      console.error('Error updating event order:', err);
      setError(err.message);
      
      // Revert local state on error
      setSelectedEvents(prev => ({
        ...prev,
        [groupId]: currentEvents
      }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(filteredGroups.length / groupsPerPage);
  const startIndex = (currentPage - 1) * groupsPerPage;
  const endIndex = startIndex + groupsPerPage;
  const currentGroups = filteredGroups.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading || loadingGroups) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Multi Race</h1>
        <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
          <button
            onClick={() => setShowNewGroup(true)}
            className="flex items-center gap-2 px-6 py-2 text-white font-medium transition-all duration-200 shadow-lg"
          >
            <Plus className="w-4 h-4" />
            New Multi Race
          </button>
        </LiquidGlass>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search multi races..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg"
          />
        </div>
      </div>

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </LiquidGlass>
      )}

      {showNewGroup && (
        <LiquidGlass variant="card" className="mb-8 p-6">
          <form onSubmit={handleCreateGroup} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Multi Race Name
              </label>
              <input
                id="name"
                type="text"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                className="mt-1 block w-full rounded-lg bg-white/60 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 px-3 py-2"
                required
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <input
                id="date"
                type="date"
                value={newGroup.date}
                onChange={(e) => setNewGroup({ ...newGroup, date: e.target.value })}
                className="mt-1 block w-full rounded-lg bg-white/60 border border-gray-200 text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 px-3 py-2"
                required
              />
            </div>

            <div className="flex gap-2">
              <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                <button
                  type="submit"
                  className="px-4 py-2 text-white font-medium"
                >
                  Create Multi Race
                </button>
              </LiquidGlass>
              <LiquidGlass variant="button" hover>
                <button
                  type="button"
                  onClick={() => setShowNewGroup(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </LiquidGlass>
            </div>
          </form>
        </LiquidGlass>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {currentGroups.map((group) => (
          <LiquidGlass key={group.id} variant="card" className="p-6 h-auto flex flex-col" hover>
            <div className="flex justify-between items-start mb-2 flex-shrink-0">
              {editingGroupId === group.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 px-2 py-1 bg-white/60 border border-gray-200 rounded-md text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                    autoFocus
                  />
                  <button
                    onClick={() => handleUpdateName(group.id)}
                    className="text-emerald-600 hover:text-emerald-700 p-1"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setEditingGroupId(null)}
                    className="text-gray-500 hover:text-gray-700 p-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 flex-1 pr-2">{group.name}</h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setShowShareModal(group.id)}
                      className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-200"
                      title="Share results"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingGroupId(group.id);
                        setEditingName(group.name);
                      }}
                      className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-200"
                      title="Edit name"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeletingGroupId(group.id)}
                      className="text-gray-500 hover:text-red-600 p-1 transition-colors duration-200"
                      title="Delete group"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <p className="text-gray-600 mb-4 flex-shrink-0">
              {formatDate(group.date)}
            </p>

            {/* Event Selection */}
            <div className="space-y-2 flex-1">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">Events</h4>
                <div className="bg-gray-100/80 backdrop-blur-sm rounded-md border border-gray-200/60">
                  <button
                    onClick={() => setShowEventSelector(showEventSelector === group.id ? null : group.id)}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 px-3 py-1.5"
                  >
                    {showEventSelector === group.id ? 'Done' : 'Add Events'}
                  </button>
                </div>
              </div>

              {showEventSelector === group.id && (
                <div className="space-y-4 mb-4">
                  <EventSearch
                    events={events}
                    selectedEvents={selectedEvents[group.id] || []}
                    onEventSelect={(eventId) => handleEventSelection(group.id, eventId)}
                    placeholder="Search events to add..."
                  />
                </div>
              )}
              
              {/* Always show sortable event list */}
              <div className="space-y-1 flex-1">
                {selectedEvents[group.id]?.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis]}
                    onDragStart={() => setActiveGroupForDnd(group.id)}
                  >
                    <SortableContext
                      items={selectedEvents[group.id]}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {events
                          .filter(event => selectedEvents[group.id].includes(event.id))
                          .sort((a, b) => {
                            const indexA = selectedEvents[group.id].indexOf(a.id);
                            const indexB = selectedEvents[group.id].indexOf(b.id);
                            return indexA - indexB;
                          })
                          .map(event => (
                            <SortableEventItem
                              key={event.id}
                              event={event}
                              groupId={group.id}
                              onRemove={() => handleRemoveEvent(group.id, event.id)}
                            />
                          ))
                        }
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-sm text-gray-500">
                    No events selected
                  </p>
                )}
              </div>
            </div>
          </LiquidGlass>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex flex-1 justify-between sm:hidden">
            <LiquidGlass variant="button" hover>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Previous
              </button>
            </LiquidGlass>
            <LiquidGlass variant="button" hover>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Next
              </button>
            </LiquidGlass>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(endIndex, filteredGroups.length)}
                </span>{' '}
                of <span className="font-medium">{filteredGroups.length}</span> multi races
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <LiquidGlass variant="button">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                </LiquidGlass>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isCurrentPage = pageNumber === currentPage;
                  
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <LiquidGlass key={pageNumber} variant="button" className={isCurrentPage ? 'bg-white/40' : ''}>
                        <button
                          onClick={() => goToPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                            isCurrentPage
                              ? 'text-gray-800'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      </LiquidGlass>
                    );
                  }
                  
                  if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                    return (
                      <span
                        key={pageNumber}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}

                <LiquidGlass variant="button">
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </LiquidGlass>
              </nav>
            </div>
          </div>
        </div>
      )}

      {deletingGroupId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-md p-6" bgOpacity="ultra">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Delete Multi Race</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this multi race? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <LiquidGlass variant="button" hover>
                <button
                  onClick={() => setDeletingGroupId(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </LiquidGlass>
              <LiquidGlass variant="button" hover glowColor="white">
                <button
                  onClick={() => handleDeleteGroup(deletingGroupId)}
                  className="px-4 py-2 text-red-600 hover:text-red-700"
                >
                  Delete Multi Race
                </button>
              </LiquidGlass>
            </div>
          </LiquidGlass>
        </div>
      )}

      {showShareModal && (
        <GroupShareModal
          group={groups.find(g => g.id === showShareModal)!}
          onClose={() => setShowShareModal(null)}
          onUpdate={loadGroups}
        />
      )}
    </div>
  );
}